// src/pages/Collection-page/CollectionPage.js
import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  MdSearch,
  MdClear,
  MdRefresh,
  MdVisibility,
  MdFileDownload,
  MdFilterList,
  MdPayment,
  MdCheckCircle,
  MdPendingActions
} from 'react-icons/md';
import {
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineCurrencyRupee,
  HiOutlineReceiptTax,
  HiOutlineClock
} from 'react-icons/hi';
import { FaMoneyBillWave, FaTimes, FaPlus, FaBuilding } from 'react-icons/fa';
import BASE_URL from '../../api';

const API_BASE = `${BASE_URL}/api`;

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#2563eb' : '#d1d5db',
    borderRadius: '0.5rem',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(37, 99, 235, 0.2)' : 'none',
    '&:hover': { borderColor: '#2563eb' },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const CollectionPage = () => {
  const token = localStorage.getItem('token');
  const [collections, setCollections] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  // View state
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'table'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  // Modals
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewCollection, setViewCollection] = useState(null);

  const [formData, setFormData] = useState({
    invoiceId: '',
    invoiceNumber: '',
    paidAmount: '',
    paymentDate: new Date().toISOString().split('T')[0]
  });

  // ────── FETCH DATA ──────
  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, colRes] = await Promise.all([
        fetch(`${API_BASE}/getctrl/invoice`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/getctrl/colection`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const [invJson, colJson] = await Promise.all([invRes.json(), colRes.json()]);

      if (invJson.success) setInvoices(invJson.data || []);
      if (colJson.success) setCollections(colJson.data || []);
    } catch (err) {
      toast.error('Failed to sync collection data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB') : 'N/A';

  // ────── INVOICE SELECTION HELPER ──────
  const handleSelectInvoice = (selectedOpt) => {
    if (!selectedOpt) {
      setFormData(prev => ({
        ...prev,
        invoiceId: '',
        invoiceNumber: '',
        paidAmount: ''
      }));
      return;
    }

    const inv = invoices.find(i => i._id === selectedOpt.value);
    if (!inv) return;

    // Calculate balance
    const totalPaid = collections
      .filter(c => c.invoiceId?._id === inv._id)
      .reduce((sum, c) => sum + (c.paidAmount || 0), 0);
    const balance = Math.max((inv.grandTotal || 0) - totalPaid, 0);

    setFormData(prev => ({
      ...prev,
      invoiceId: inv._id,
      invoiceNumber: inv.invoiceNumber,
      paidAmount: balance > 0 ? balance.toFixed(2) : ''
    }));
  };

  // ────── RECORD PAYMENT ──────
  const handleAddCollection = async () => {
    if (!formData.invoiceId || !formData.paidAmount) {
      return toast.error('Please select an invoice and enter paid amount');
    }

    try {
      const res = await fetch(`${API_BASE}/getctrl/colection`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, paidAmount: Number(formData.paidAmount) })
      });
      const json = await res.json();
      if (json.success) {
        toast.success('Payment successfully recorded!');
        setShowAddForm(false);
        setFormData({
          invoiceId: '',
          invoiceNumber: '',
          paidAmount: '',
          paymentDate: new Date().toISOString().split('T')[0]
        });
        fetchData();
      } else {
        toast.error(json.message || 'Failed to record payment');
      }
    } catch (err) {
      toast.error('Network connection error');
    }
  };

  // ────── FILTER & PAGINATION ──────
  const filtered = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    return collections.filter(col => {
      const client = col.invoiceId?.clientId || {};
      const clientName = (client.companyName || client.name || '').toLowerCase();
      const invNo = (col.invoiceNumber || '').toLowerCase();
      const statusStr = (col.status || '').toLowerCase();

      const matchesSearch =
        !s ||
        invNo.includes(s) ||
        clientName.includes(s) ||
        statusStr.includes(s);

      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'COLLECTED' && col.status === 'Collected') ||
        (statusFilter === 'PENDING' && col.status !== 'Collected');

      return matchesSearch && matchesStatus;
    });
  }, [collections, searchTerm, statusFilter]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setCurrentPage(1);
  };

  // KPI calculations
  const totalCollections = collections.length;
  const totalPaidSum = collections.reduce((sum, c) => sum + (c.paidAmount || 0), 0);
  const collectedCount = collections.filter(c => c.status === 'Collected').length;
  const pendingCount = totalCollections - collectedCount;

  // ────── EXCEL EXPORT ──────
  const exportToExcel = () => {
    if (collections.length === 0) {
      toast.warn('No collections to export');
      return;
    }

    const exportData = filtered.map((col, i) => {
      const client = col.invoiceId?.clientId || {};
      const clientName = client.companyName || client.name || 'N/A';
      const grandTotal = col.invoiceId?.grandTotal || 0;
      const totalPaidForInv = collections
        .filter(c => c.invoiceId?._id === col.invoiceId?._id)
        .reduce((sum, c) => sum + (c.paidAmount || 0), 0);
      const outstanding = Math.max(grandTotal - totalPaidForInv, 0);

      return {
        'S.No': i + 1,
        'Invoice No': col.invoiceNumber || '',
        'Client': clientName,
        'Bill Total (₹)': grandTotal,
        'Paid Amount (₹)': col.paidAmount || 0,
        'Outstanding Balance (₹)': outstanding,
        'Payment Date': formatDate(col.paymentDate),
        'Status': col.status || 'Pending',
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Collections');
    XLSX.writeFile(workbook, `Collections_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success('Collections exported to Excel!');
  };

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Page Title & Breadcrumb Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
            <MdPayment className="text-blue-600 text-3xl" />
            Payment Collections
          </h2>
          <p className="text-xs md:text-sm text-gray-500 mt-1">
            Track invoice payments, record receipts, and monitor outstanding receivables
          </p>
        </div>

        <button
          onClick={() => setShowAddForm(true)}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-all duration-150 flex items-center gap-2 text-sm uppercase tracking-wide cursor-pointer w-fit"
        >
          <FaPlus size={14} /> Record Payment
        </button>
      </div>

      {/* KPI Summary Quick Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineReceiptTax />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Transactions</p>
            <p className="text-2xl font-extrabold text-gray-900">{totalCollections}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center text-2xl font-bold">
            <HiOutlineCurrencyRupee />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Total Collected</p>
            <p className="text-2xl font-extrabold text-emerald-600">
              ₹{totalPaidSum.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-2xl font-bold">
            <MdCheckCircle />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Fully Collected</p>
            <p className="text-2xl font-extrabold text-green-600">{collectedCount}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4 hover:shadow-md transition">
          <div className="w-12 h-12 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center text-2xl font-bold">
            <MdPendingActions />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Pending / Partial</p>
            <p className="text-2xl font-extrabold text-amber-600">{pendingCount}</p>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-5 md:p-6">
        {/* Unified Control Toolbar */}
        <div className="flex flex-col gap-4 mb-6 pb-5 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-lg">
              <MdSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
              <input
                type="text"
                placeholder="Search by invoice no, client or status..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-10 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <MdClear size={18} />
                </button>
              )}
            </div>

            {/* View Switcher, Entries Per Page & Excel Export */}
            <div className="flex items-center flex-wrap gap-2.5">
              {/* Entries per page */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="py-2 px-2.5 bg-gray-50 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={6}>6 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center bg-gray-100 p-1 rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => setViewMode('card')}
                  className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                    viewMode === 'card'
                      ? 'bg-white text-blue-600 shadow-sm font-bold'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Landscape Card View"
                >
                  <HiOutlineViewGrid size={18} />
                  <span className="hidden sm:inline">Cards</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md flex items-center gap-1.5 text-xs font-medium transition cursor-pointer ${
                    viewMode === 'table'
                      ? 'bg-white text-blue-600 shadow-sm font-bold'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title="Table List View"
                >
                  <HiOutlineViewList size={18} />
                  <span className="hidden sm:inline">Table</span>
                </button>
              </div>

              {/* Export Excel Button */}
              <button
                onClick={exportToExcel}
                className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm transition cursor-pointer"
                title="Export Collections to Excel"
              >
                <MdFileDownload size={18} />
                <span className="hidden sm:inline">Export Excel</span>
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <span className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
              <MdFilterList /> Filters:
            </span>

            {/* Filter by Status */}
            <select
              value={statusFilter}
              onChange={e => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="py-1.5 px-3 bg-gray-50 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="COLLECTED">Collected</option>
              <option value="PENDING">Pending</option>
            </select>

            {(searchTerm || statusFilter !== 'ALL') && (
              <button
                onClick={resetFilters}
                className="py-1.5 px-3 text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1 cursor-pointer"
              >
                <MdRefresh /> Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        {loading ? (
          <div className="text-center py-16 text-gray-500">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent mb-3"></div>
            <p className="text-sm font-medium">Loading collection receipts...</p>
          </div>
        ) : paginated.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <FaMoneyBillWave className="mx-auto text-5xl text-gray-300 mb-2" />
            <p className="text-gray-700 font-bold text-base">No collection records found</p>
            <p className="text-gray-500 text-xs mt-1">Try adjusting your search criteria or record a new payment.</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition"
            >
              Record First Payment
            </button>
          </div>
        ) : viewMode === 'card' ? (
          /* LANDSCAPE CARD VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-5">
            {paginated.map(col => {
              const client = col.invoiceId?.clientId || {};
              const clientName = client.companyName || client.name || 'N/A';
              const grandTotal = col.invoiceId?.grandTotal || 0;
              const isCollected = col.status === 'Collected';

              const totalPaidForInv = collections
                .filter(c => c.invoiceId?._id === col.invoiceId?._id)
                .reduce((sum, c) => sum + (c.paidAmount || 0), 0);
              const balance = Math.max(grandTotal - totalPaidForInv, 0);

              return (
                <div
                  key={col._id}
                  className="bg-white rounded-xl border border-gray-200 hover:border-blue-400 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col justify-between overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-blue-50/40 px-5 py-3.5 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-sm shrink-0">
                        <MdPayment size={20} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-extrabold text-blue-700 truncate tracking-wide">
                          {col.invoiceNumber}
                        </h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              isCollected
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {col.status?.toUpperCase() || 'PENDING'}
                          </span>
                          <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <HiOutlineClock /> {formatDate(col.paymentDate)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <button
                      onClick={() => setViewCollection(col)}
                      className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-white rounded-lg transition"
                      title="View Receipt Details"
                    >
                      <MdVisibility size={18} />
                    </button>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-4 flex-1">
                    {/* Client info */}
                    <div>
                      <p className="text-[10px] font-bold uppercase text-gray-400 flex items-center gap-1">
                        <FaBuilding /> Client / Account
                      </p>
                      <p className="text-sm font-bold text-gray-900 truncate mt-0.5">
                        {clientName}
                      </p>
                    </div>

                    {/* Financial Metrics */}
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                        <p className="text-[10px] uppercase font-bold text-gray-500">Bill Total</p>
                        <p className="text-sm font-extrabold text-gray-800 mt-0.5">
                          ₹{grandTotal.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-emerald-50 p-2.5 rounded-lg border border-emerald-100">
                        <p className="text-[10px] uppercase font-bold text-emerald-700">Paid Here</p>
                        <p className="text-sm font-extrabold text-emerald-800 mt-0.5">
                          ₹{(col.paidAmount || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-rose-50 p-2.5 rounded-lg border border-rose-100">
                        <p className="text-[10px] uppercase font-bold text-rose-700">Balance</p>
                        <p className="text-sm font-extrabold text-rose-800 mt-0.5">
                          ₹{balance.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="bg-gray-50 px-5 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <span>Payment Receipt</span>
                    <button
                      onClick={() => setViewCollection(col)}
                      className="text-blue-600 hover:text-blue-800 font-semibold cursor-pointer"
                    >
                      View Receipt &rarr;
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* TABLE LIST VIEW */
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white uppercase text-xs font-semibold tracking-wider">
                <tr>
                  <th className="p-3.5 text-left">S.No</th>
                  <th className="p-3.5 text-left">Invoice No</th>
                  <th className="p-3.5 text-left">Client</th>
                  <th className="p-3.5 text-right">Bill Total</th>
                  <th className="p-3.5 text-right">Paid Amount</th>
                  <th className="p-3.5 text-right">Remaining Balance</th>
                  <th className="p-3.5 text-left">Payment Date</th>
                  <th className="p-3.5 text-left">Status</th>
                  <th className="p-3.5 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-xs">
                {paginated.map((col, i) => {
                  const sNo = (currentPage - 1) * pageSize + i + 1;
                  const client = col.invoiceId?.clientId || {};
                  const clientName = client.companyName || client.name || 'N/A';
                  const grandTotal = col.invoiceId?.grandTotal || 0;
                  const totalPaidForInv = collections
                    .filter(c => c.invoiceId?._id === col.invoiceId?._id)
                    .reduce((sum, c) => sum + (c.paidAmount || 0), 0);
                  const balance = Math.max(grandTotal - totalPaidForInv, 0);

                  return (
                    <tr key={col._id} className="hover:bg-blue-50/50 transition">
                      <td className="p-3.5 font-bold text-gray-500">{sNo}</td>
                      <td className="p-3.5 font-extrabold text-blue-700 text-sm">
                        {col.invoiceNumber}
                      </td>
                      <td className="p-3.5 font-semibold text-gray-900">{clientName}</td>
                      <td className="p-3.5 text-right font-semibold text-gray-700">
                        ₹{grandTotal.toLocaleString()}
                      </td>
                      <td className="p-3.5 text-right font-extrabold text-emerald-600 text-sm">
                        ₹{(col.paidAmount || 0).toLocaleString()}
                      </td>
                      <td className="p-3.5 text-right font-extrabold text-rose-600 text-sm">
                        ₹{balance.toLocaleString()}
                      </td>
                      <td className="p-3.5 text-gray-600">{formatDate(col.paymentDate)}</td>
                      <td className="p-3.5">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${
                            col.status === 'Collected'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}
                        >
                          {col.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </td>
                      <td className="p-3.5 text-center">
                        <button
                          onClick={() => setViewCollection(col)}
                          className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="View Receipt Details"
                        >
                          <MdVisibility size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {filtered.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 text-xs text-gray-600">
            <div>
              Showing{' '}
              <span className="font-bold text-gray-800">
                {(currentPage - 1) * pageSize + 1}
              </span>{' '}
              to{' '}
              <span className="font-bold text-gray-800">
                {Math.min(currentPage * pageSize, filtered.length)}
              </span>{' '}
              of <span className="font-bold text-gray-800">{filtered.length}</span> entries
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((p, idx, arr) => {
                  const prevVal = arr[idx - 1];
                  return (
                    <React.Fragment key={p}>
                      {prevVal && p - prevVal > 1 && <span className="px-1 text-gray-400">...</span>}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={`px-3 py-1.5 rounded-lg font-bold transition cursor-pointer ${
                          currentPage === p
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* VIEW COLLECTION RECEIPT MODAL */}
      {viewCollection && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center text-white font-bold text-lg">
                  <MdPayment />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg text-white">
                    Payment Receipt
                  </h3>
                  <p className="text-xs text-blue-100">
                    Invoice #{viewCollection.invoiceNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setViewCollection(null)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition cursor-pointer"
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 text-xs">
              <div className="flex items-center justify-between bg-gray-50 p-3 rounded-xl border border-gray-200">
                <div>
                  <span className="text-[10px] font-bold text-gray-500 uppercase block">Payment Date</span>
                  <span className="font-bold text-gray-800">{formatDate(viewCollection.paymentDate)}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-bold text-gray-500 uppercase block">Status</span>
                  <span
                    className={`font-bold px-2 py-0.5 rounded-full text-[10px] ${
                      viewCollection.status === 'Collected'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {viewCollection.status?.toUpperCase() || 'PENDING'}
                  </span>
                </div>
              </div>

              <div className="p-4 rounded-xl border border-blue-100 bg-blue-50/40">
                <p className="text-[10px] font-bold text-blue-800 uppercase mb-1">Client Account</p>
                <p className="font-extrabold text-gray-900 text-sm">
                  {viewCollection.invoiceId?.clientId?.companyName || 'N/A'}{' '}
                  {viewCollection.invoiceId?.clientId?.name ? `(${viewCollection.invoiceId.clientId.name})` : ''}
                </p>
                <p className="text-gray-600 mt-1">
                  Station: {viewCollection.invoiceId?.clientId?.station || 'N/A'} • GST: {viewCollection.invoiceId?.clientId?.gstNumber || 'N/A'}
                </p>
              </div>

              <div className="border border-gray-200 rounded-xl p-4 space-y-2">
                <div className="flex justify-between py-1 border-b">
                  <span className="text-gray-600">Invoice Grand Total:</span>
                  <span className="font-bold text-gray-800">
                    ₹{(viewCollection.invoiceId?.grandTotal || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-emerald-700 font-bold">Amount Paid (This Transaction):</span>
                  <span className="font-extrabold text-emerald-800 text-sm">
                    ₹{(viewCollection.paidAmount || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200 rounded-b-2xl">
              <button
                onClick={() => setViewCollection(null)}
                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-bold uppercase transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RECORD PAYMENT MODAL */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 md:p-8 shadow-2xl relative border border-gray-200">
            <button
              onClick={() => setShowAddForm(false)}
              className="absolute top-5 right-5 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
            >
              <FaTimes size={20} />
            </button>

            <div className="border-b border-gray-200 pb-3 mb-5">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FaMoneyBillWave className="text-green-600" />
                Record Payment Receipt
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Apply collection payment to an invoice and calculate remaining balance
              </p>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                  Select Invoice *
                </label>
                <Select
                  placeholder="Search invoice no or client..."
                  options={invoices.map(inv => {
                    const client = inv.clientId || {};
                    const clientName = client.companyName || client.name || 'Unknown';
                    const totalPaid = collections
                      .filter(c => c.invoiceId?._id === inv._id)
                      .reduce((sum, c) => sum + (c.paidAmount || 0), 0);
                    const bal = Math.max((inv.grandTotal || 0) - totalPaid, 0);

                    return {
                      value: inv._id,
                      label: `${inv.invoiceNumber} - ${clientName} (Bal: ₹${bal.toLocaleString()})`,
                      balance: bal
                    };
                  })}
                  value={
                    formData.invoiceId
                      ? {
                          value: formData.invoiceId,
                          label: formData.invoiceNumber
                        }
                      : null
                  }
                  onChange={handleSelectInvoice}
                  styles={customSelectStyles}
                  isClearable
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Paid Amount (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.paidAmount}
                    onChange={e => setFormData({ ...formData, paidAmount: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-xs font-bold text-emerald-700 bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">
                    Payment Date *
                  </label>
                  <input
                    type="date"
                    value={formData.paymentDate}
                    onChange={e => setFormData({ ...formData, paymentDate: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-xs bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold text-xs uppercase transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddCollection}
                  className="px-7 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-xs uppercase shadow-md transition cursor-pointer"
                >
                  Save Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionPage;